import { ArrowLeft } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { CONTACT_EMAIL } from "@/i18n/contactDetails";

const H = ({ children }) => (
    <h2 className="mt-12 font-display text-2xl tracking-tight text-bone">{children}</h2>
);
const P = ({ children }) => (
    <p className="mt-4 text-sm leading-relaxed text-ash">{children}</p>
);
const LI = ({ children }) => (
    <li className="mt-2 text-sm leading-relaxed text-ash">{children}</li>
);

const PRIVACY = {
    title: "Privacyverklaring",
    intro: "Clarke Business Solutions (KvK 42207779, 'Clarke', 'wij') verwerkt persoonsgegevens voor onze adviesdiensten op het gebied van management en bedrijfsvoering en voor het leveren van het Clarke-platform aan maakbedrijven en foodproducenten. In deze verklaring lees je welke gegevens we verwerken, waarom en welke rechten je hebt. Laatste update: september 2026.",
    sections: [
        {
            h: "1. Welke gegevens verwerken wij",
            body: [
                "Accountgegevens: naam, e-mailadres en functie van gebruikers van het platform.",
                "Bedrijfsgegevens die klanten zelf invoeren: productieorders, recepturen, voorraad, klantcontacten (CRM), offertes en facturen.",
                "Personeelsgegevens die klanten zelf invoeren: namen, roosters, beschikbaarheid en verlofregistratie.",
                "Technische gegevens: logbestanden en beveiligingsregistraties (bijv. mislukte inlogpogingen).",
            ],
        },
        {
            h: "2. Doeleinden en grondslagen",
            body: [
                "Het leveren van de dienst (uitvoering van de overeenkomst): accounts, modules, offertes, facturen en klantportaal.",
                "Communicatie: het versturen van offertes, facturen, herinneringen en systeemberichten per e-mail.",
                "Beveiliging en misbruikpreventie (gerechtvaardigd belang): wachtwoord-herstel, inlogbeveiliging en foutanalyse.",
                "Wettelijke verplichtingen: fiscale bewaarplicht van facturatiegegevens (7 jaar).",
            ],
        },
        {
            h: "3. Voor klantgegevens geldt",
            body: [
                "Gegevens die een klant over zijn eigen klanten en medewerkers invoert, verwerken wij uitsluitend als verwerker op instructie van die klant (de verwerkingsverantwoordelijke). Hiervoor geldt onze Verwerkersovereenkomst.",
            ],
        },
        {
            h: "4. Bewaartermijnen",
            body: [
                "Account- en bedrijfsgegevens: zolang het abonnement loopt. Na beëindiging ontvangt de klant een volledige export en verwijderen wij de gegevens binnen 60 dagen.",
                "Facturatiegegevens: 7 jaar (wettelijke fiscale bewaarplicht).",
                "Deellinks naar documenten: maximaal 30 dagen, daarna automatisch verwijderd.",
            ],
        },
        {
            h: "5. Derden (subverwerkers)",
            body: [
                "Cloudhosting en database (EU-infrastructuur van onze platformleverancier).",
                "E-mailverzending via Resend (transactionele e-mails zoals offertes en wachtwoord-herstel).",
                "Boekhoudkoppelingen (Moneybird of e-Boekhouden) — alleen actief wanneer een klant deze zelf koppelt met een eigen API-token.",
                "Wij verkopen nooit gegevens aan derden en gebruiken ze niet voor advertenties.",
            ],
        },
        {
            h: "6. Beveiliging",
            body: [
                "Versleutelde verbindingen (TLS), wachtwoorden onleesbaar opgeslagen (bcrypt), strikte scheiding per klant-omgeving, rolgebaseerde toegang en beveiligde, verlopende deellinks voor documenten.",
            ],
        },
        {
            h: "7. Jouw rechten",
            body: [
                "Je hebt recht op inzage, correctie, verwijdering, beperking, bezwaar en dataportabiliteit. Klanten van het platform kunnen zelf een volledige data-export downloaden via Platform → Privacy & gegevens.",
                `Verzoeken of vragen: ${CONTACT_EMAIL}. Je kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl).`,
            ],
        },
    ],
};

const DPA = {
    title: "Verwerkersovereenkomst",
    intro: "Deze verwerkersovereenkomst maakt integraal deel uit van de overeenkomst tussen Clarke Business Solutions (KvK 42207779, 'Verwerker') en de klant ('Verwerkingsverantwoordelijke') en heeft betrekking op de verwerking van persoonsgegevens via het Clarke-platform. Laatste update: september 2026.",
    sections: [
        {
            h: "Artikel 1 — Onderwerp en duur",
            body: [
                "Verwerker verwerkt persoonsgegevens uitsluitend in opdracht van Verwerkingsverantwoordelijke, voor de duur van de SaaS-overeenkomst, ten behoeve van het leveren van het platform (productiedata, CRM, personeelsplanning, facturatie en klantportaal).",
            ],
        },
        {
            h: "Artikel 2 — Aard, doeleinden en categorieën",
            body: [
                "Aard van de verwerking: opslag, weergave, bewerking en verzending (per e-mail) van door Verwerkingsverantwoordelijke ingevoerde gegevens.",
                "Soorten persoonsgegevens: namen, e-mailadressen en telefoonnummers van contactpersonen; namen, roosters, beschikbaarheid en verlof van medewerkers; factuur- en ordergegevens.",
                "Categorieën betrokkenen: klanten en prospects van Verwerkingsverantwoordelijke, medewerkers van Verwerkingsverantwoordelijke en eindklanten die het klantportaal gebruiken.",
                "Bijzondere persoonsgegevens worden niet beoogd te verwerken.",
            ],
        },
        {
            h: "Artikel 3 — Verplichtingen van Verwerker",
            body: [
                "Verwerker verwerkt persoonsgegevens uitsluitend op gedocumenteerde instructie van Verwerkingsverantwoordelijke, waaronder deze overeenkomst.",
                "Verwerker garandeert vertrouwelijkheid van alle medewerkers en onderaannemers die toegang hebben tot de gegevens.",
                "Verwerker assisteert Verwerkingsverantwoordelijke, voor zover redelijkerwijs mogelijk, bij verzoeken van betrokkenen (inzage, correctie, verwijdering, dataportabiliteit). De data-export in het platform ondersteunt dit rechtstreeks.",
            ],
        },
        {
            h: "Artikel 4 — Beveiligingsmaatregelen",
            body: [
                "Versleuteling van verbindingen (TLS) en van gevoelige integratiegegevens (API-tokens) in de database.",
                "Strikte tenant-isolatie: gegevens van elke klant zijn logisch gescheiden en uitsluitend toegankelijk na authenticatie binnen die omgeving.",
                "Rolgebaseerde toegang (eigenaar/medewerker), beveiligde sessies, inlogbeveiliging met vertraging bij herhaalde mislukte pogingen.",
                "Deellinks naar documenten zijn versleuteld gegenereerd en verlopen automatisch.",
            ],
        },
        {
            h: "Artikel 5 — Subverwerkers",
            body: [
                "Verwerkingsverantwoordelijke machtigt Verwerker om de volgende subverwerkers in te schakelen: de cloudhosting-/databaseleverancier van het platform en Resend voor transactionele e-mail.",
                "Bij het aangaan van een nieuwe subverwerker informeert Verwerker Verwerkingsverantwoordelijke vooraf; Verwerkingsverantwoordelijke kan binnen 14 dagen gemotiveerd bezwaar maken.",
            ],
        },
        {
            h: "Artikel 6 — Datalekken",
            body: [
                "Verwerker meldt een datalek met een redelijke kans op risico voor betrokkenen zonder onredelijke vertraging, en uiterlijk binnen 72 uur na constatering, aan Verwerkingsverantwoordelijke, met de aard van het lek, de vermoedelijke omvang en de genomen maatregelen.",
            ],
        },
        {
            h: "Artikel 7 — Einde van de dienstverlening",
            body: [
                "Na einde van de overeenkomst stelt Verwerker een volledige export van de gegevens beschikbaar en verwijdert Verwerker de persoonsgegevens binnen 60 dagen, tenzij wettelijke bewaarplicht (zoals voor facturen) langere bewaring vereist.",
            ],
        },
        {
            h: "Artikel 8 — Aansprakelijkheid en slotbepalingen",
            body: [
                "De aansprakelijkheidsbeperking uit de SaaS-overeenkomst is van overeenkomstige toepassing. Op deze verwerkersovereenkomst is Nederlands recht van toepassing.",
                `Vragen over deze overeenkomst: ${CONTACT_EMAIL}.`,
            ],
        },
    ],
};

export default function LegalPage({ type = "privacy" }) {
    const doc = type === "dpa" ? DPA : PRIVACY;
    return (
        <div data-testid={`legal-page-${type}`} className="min-h-screen bg-ink text-bone">
            <div className="noise-overlay" aria-hidden="true" />
            <header className="border-b border-white/10">
                <div className="mx-auto flex max-w-[900px] items-center justify-between px-6 py-5">
                    <BrandLockup emblemClass="h-12 w-12" textClass="text-xl" align="start" />
                    <a
                        data-testid="legal-back-link"
                        href="/"
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar site
                    </a>
                </div>
            </header>
            <main className="mx-auto max-w-[900px] px-6 py-16">
                <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Clarke Business Solutions</p>
                <h1 className="mt-4 font-display text-4xl tracking-tight md:text-5xl">{doc.title}</h1>
                <P>{doc.intro}</P>
                {doc.sections.map((s) => (
                    <section key={s.h}>
                        <H>{s.h}</H>
                        <ul className="list-none">
                            {s.body.map((p, i) => (
                                <LI key={i}>{p}</LI>
                            ))}
                        </ul>
                    </section>
                ))}
                <div className="mt-16 flex flex-wrap gap-6 border-t border-white/10 pt-8 text-[11px] uppercase tracking-[0.25em] text-ash">
                    <span data-testid="legal-company-details" className="normal-case tracking-normal text-ash/70">
                        Clarke Business Solutions · KvK 42207779 · SBI 70202 (hoofdactiviteit), 62100 & 82400 (nevenactiviteiten)
                    </span>
                    <a data-testid="legal-link-privacy" href="/privacy" className="transition-colors hover:text-gold">Privacyverklaring</a>
                    <a data-testid="legal-link-dpa" href="/verwerkersovereenkomst" className="transition-colors hover:text-gold">Verwerkersovereenkomst</a>
                    <a data-testid="legal-link-contact" href={`mailto:${CONTACT_EMAIL}`} className="transition-colors hover:text-gold">{CONTACT_EMAIL}</a>
                </div>
            </main>
        </div>
    );
}
